import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  FormTextarea,
  FormSelect
} from "protocol-setup/components/FormComponents";
import { useOpenAdminContactList } from "protocol-setup/redux/useLovs";
import { postWithAuth } from "protocol-setup/redux/makeFetch";
import {
  defaultErrorMessage,
  openRaveSubmittedUrl
} from "protocol-setup/redux/constants";
import errorMessages, { COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";
import {
  OpenErrorNotification,
  OpenSuccessNotification
} from "protocol-setup/components/notification";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { tabNames } from "../orHelpers";

export const handleSubmitForm = async (
  data,
  requestNumber,
  onSuccessCallback
) => {
  try {
    const payload = {
      requestNumber,
      contactDto: [
        {
          perId: data.primaryOpenAdmin,
          contactType: "PRIMARY_OPEN_ADMIN",
          comments: data.primaryComments
        },
        {
          perId: data.secondaryOpenAdmin,
          contactType: "SECONDARY_OPEN_ADMIN",
          comments: data.secondaryComments
        }
      ]
    };
    await postWithAuth(openRaveSubmittedUrl, payload);
    if (onSuccessCallback) {
      await onSuccessCallback();
    }
    OpenSuccessNotification({
      message: "Record successfully created.",
      autoHide: true
    });
    return true;
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
    return false;
  }
};

export const schema = yup.object().shape({
  primaryOpenAdmin: yup
    .string()
    .required("Required Field"),
  secondaryOpenAdmin: yup
    .string()
    .required("Required Field"),
  primaryComments: yup.string().max(COMMENTS_MAX_LENGTH, errorMessages.common.comments.max).optional(),
  secondaryComments: yup.string().max(COMMENTS_MAX_LENGTH, errorMessages.common.comments.max).optional()
});

const OpenRaveSubmitForm = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, protSetupContacts } = protocolSetupData;

  const contactListResp = useOpenAdminContactList();
  const contactList = contactListResp?.openAdminContactList?.data || [];
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver: yupResolver(schema),

    defaultValues: {
      primaryOpenAdmin:
        protSetupContacts?.find((c) => c.contactType === "PRIMARY_OPEN_ADMIN")
          ?.perId || "",
      primaryComments:
        protSetupContacts?.find((c) => c.contactType === "PRIMARY_OPEN_ADMIN")
          ?.comments || "",
      secondaryOpenAdmin:
        protSetupContacts?.find((c) => c.contactType === "SECONDARY_OPEN_ADMIN")
          ?.perId || "",
      secondaryComments:
        protSetupContacts?.find((c) => c.contactType === "SECONDARY_OPEN_ADMIN")
          ?.comments || ""
    }
  });

  const hasSignOffPermission = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_SUBMITTED);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, onSuccessCallback),
    dirtyFields,
    tabNames.SIGNOFF_SUBMITTED,
    "Signoff"
  );

  const onReset = () => {
    reset();
  };

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${
        readOnly ? "read-only" : ""
      }`}
    >
      <form
        id="OpenRaveSignoffSubmitted"
        className="bg-transparent"
        onSubmit={handleSubmit((data) => {
          const d = {...data, requestNumber}
          if(d.secondaryOpenAdmin === d.primaryOpenAdmin) {
            OpenErrorNotification({
              message: errorMessages.openRave.openRaveSubmitForm.duplicateCtsuUser,
              autoHide: true
            });
            return;
          }
          onSubmitWithHistory(d, onSuccess)
        }
      )}
      >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul className="row">
            <ul className="col-4 mr-4 mb-0 form-sub-group">
              <li className="d-block">
                <div className="content-group-title">Primary OPEN Admin</div>
              </li>
              <li className={`d-block ${!isSignOffEditable ? 'mb-4' : ''}`}>
                <FormSelect
                  name="primaryOpenAdmin"
                  data-testid="form-select-primaryOpenAdmin"
                  section="openRave"
                  form="openRaveSubmitForm"
                  control={control}
                  options={contactList?.map((item) => ({
                    value: item.perId,
                    label: `${item.lastName}, ${item.firstName} (${item.ctepPersonId})`
                  }))}
                  required
                  placeholder="Select an option"
                  readOnly={!isSignOffEditable}
                  trigger={trigger}
                />
              </li>
              <li className="d-block">
                <FormTextarea
                  name="primaryComments"
                  section="openRave"
                  form="openRaveSubmitForm"
                  control={control}
                  rows={3}
                  disabled={!isSignOffEditable}
                  trigger={trigger}
                />
              </li>
            </ul>

            <ul
              className="col-4 mr-4 mb-0 form-sub-group"
              role="group"
            >
              <li className="d-block">
                <div className="content-group-title">Secondary OPEN Admin</div>
              </li>
              <li className={`d-block ${!isSignOffEditable ? 'mb-4' : ''}`}>
                <FormSelect
                  name="secondaryOpenAdmin"
                  data-testid="form-select-secondaryOpenAdmin"
                  section="openRave"
                  form="openRaveSubmitForm"
                  control={control}
                  options={contactList?.map((item) => ({
                    value: item.perId,
                    label: `${item.lastName}, ${item.firstName} (${item.ctepPersonId})`
                  }))}
                  required
                  placeholder="Select an option"
                  readOnly={!isSignOffEditable}
                  trigger={trigger}
                />
              </li>
              <li className="d-block">
                <FormTextarea
                  name="secondaryComments"
                  section="openRave"
                  form="openRaveSubmitForm"
                  control={control}
                  rows={3}
                  disabled={!isSignOffEditable}
                  trigger={trigger}
                />
              </li>
            </ul>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          {isSignOffEditable && (
            <ButtonGroupSignoff
              onSubmit={handleSubmitForm}
              onReset={onReset}
              isSubmitting={isSubmitting}
              control={control}
              protectionElement= {ProtectionElements.OPEN_SIGNOFF_SUBMITTED }
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default OpenRaveSubmitForm;
